import React, { useCallback, useMemo } from "react";
import formatCurrency from "@tvg/formatter/currency";
import { useColorTokens } from "@tvg/design-system/web/hooks";
import { get } from "lodash";
import { Saddle } from "../saddle";
import {
  IconContainer,
  SaddleDetailsParagraph,
  SaddleSelectionContainer,
  RowContainer,
  WillPaysContainer,
  AlternateContainer,
  AlternateDivider,
  IconContainerAlternate,
  IconWrapper
} from "./styled-components";
import { rangeSelections } from "./utils";
import { ShowSelectionsProps } from "./types";
import { Paragraph } from "../typography";
import { Tag } from "../tag";
import { ParagraphFonts } from "../typography/paragraph/types";
import Icon from "../icon";

const SaddleNumbers = ({
  numWagerableRunners,
  selections,
  isDark,
  showBetType,
  scratches,
  wagerDetails = false,
  shouldShowRefundTag,
  breed = "thoroughbred",
  onHandicaping,
  hideWillPaysTag = false,
  enableMyBetsBehavior = false,
  showOdds,
  favoriteRunner,
  showReplacedRunners,
  raceStatus,
  isAlternateView,
  showAlternatePlaceholder = true
}: ShowSelectionsProps) => {
  const colorTokens = useColorTokens();

  const saddleSelections =
    selections?.length > 0
      ? selections
          .filter((selection) => wagerDetails || !selection.isAlternate)
          .sort(
            (a, b) =>
              parseInt(a.number.toString(), 10) -
              parseInt(b.number.toString(), 10)
          )
      : [];

  const hasScratch = scratches ? scratches?.length > 0 : false;
  const hasWinner = saddleSelections.some((selection) => selection.winner);
  const hasWillPays = saddleSelections.some((selection) => selection.willPay);

  const checkSaddleSelection = useCallback(
    (runnerNumber: string) =>
      saddleSelections.filter(
        (saddleSelection) => saddleSelection.number === runnerNumber
      ),
    [saddleSelections, hasWinner, hasScratch, hasWillPays]
  );

  const alternateSelections = useMemo(
    () =>
      selections.filter((selection) => selection.isAlternate && !wagerDetails),
    [selections]
  );

  const isAllSelections =
    numWagerableRunners &&
    Number(numWagerableRunners) === saddleSelections.length &&
    !wagerDetails;

  const isSaddleNumber = !isAllSelections && saddleSelections.length > 0;

  const rangedSelections: string[] = useMemo(
    () =>
      rangeSelections(
        saddleSelections,
        scratches,
        wagerDetails,
        enableMyBetsBehavior
      )
        .join()
        .split(/(-)/)
        .join()
        .split(","),
    [wagerDetails, saddleSelections, scratches]
  );

  const renderAllSelections = () =>
    isAllSelections ? (
      <Saddle all={isAllSelections} number="All" size="s" breed={breed} />
    ) : (
      // empty Selections
      <Saddle
        number="?"
        size="s"
        saddleColor={colorTokens.content.default}
        numberColor={colorTokens.component.input.base}
        mr="space-1"
        enableMyBetsBehavior={enableMyBetsBehavior}
      />
    );

  return (
    <>
      {!isSaddleNumber &&
      !hasScratch &&
      !onHandicaping &&
      !wagerDetails &&
      !hasWinner ? (
        renderAllSelections()
      ) : (
        <SaddleSelectionContainer
          isDetailView={wagerDetails}
          hasThirdColumn={!!shouldShowRefundTag || hasWillPays}
        >
          {rangedSelections.map((item, index) => {
            const currentSaddleSelection = checkSaddleSelection(item)[0];

            const runnerDetailsStyle = {
              fontFamily: (currentSaddleSelection?.winner &&
              !currentSaddleSelection?.scratched
                ? "bold"
                : "medium") as ParagraphFonts,
              opacity: currentSaddleSelection?.isSettled ? "0.3" : "1"
            };

            return (
              <RowContainer
                isDetailView={wagerDetails}
                showReplacedRunners={
                  showReplacedRunners && scratches?.includes(item)
                }
                key={`sanddle-arrow-${index.toString()}`}
              >
                {item.includes("-") ? (
                  <IconContainer>
                    <Icon
                      size="m"
                      name="arrowRight"
                      lineColor={
                        isDark && showBetType
                          ? "content.onDark"
                          : "content.subtle"
                      }
                      qaLabel="runner-number-range-arrow"
                      mr="space-1"
                      shouldUseColorTokens
                    />
                  </IconContainer>
                ) : (
                  currentSaddleSelection && (
                    <>
                      <Saddle
                        isScratched={
                          scratches?.includes(item) ||
                          (!enableMyBetsBehavior &&
                            hasWinner &&
                            currentSaddleSelection.winner === false)
                        }
                        raceFinished={
                          saddleSelections.length === scratches?.length ||
                          (hasWinner &&
                            currentSaddleSelection.winner === false) ||
                          (enableMyBetsBehavior &&
                            ["lost", "won", "attention"].includes(
                              raceStatus ?? ""
                            ))
                        }
                        key={`Saddle-num-${currentSaddleSelection.number}`}
                        number={currentSaddleSelection.number}
                        numberColor={currentSaddleSelection.numberColor}
                        saddleColor={currentSaddleSelection.saddleColor}
                        all={currentSaddleSelection.number === "ALL"}
                        isWinner={currentSaddleSelection.winner}
                        breed={breed}
                        enableMyBetsBehavior={enableMyBetsBehavior}
                        size="s"
                        mr={
                          !showBetType && index === rangedSelections.length
                            ? 0
                            : "space-1"
                        }
                      />

                      {showReplacedRunners &&
                        scratches?.includes(item) &&
                        enableMyBetsBehavior && (
                          <>
                            <IconContainer
                              key={`sanddle-arrow-${index.toString()}`}
                            >
                              <Icon
                                size="m"
                                name="arrowRight"
                                lineColor="background.warning"
                                qaLabel="runner-number-range-arrow"
                                mr="space-1"
                                shouldUseColorTokens
                              />
                            </IconContainer>
                            <Saddle
                              isScratched={
                                scratches?.includes(item) ||
                                (!enableMyBetsBehavior &&
                                  hasWinner &&
                                  currentSaddleSelection.winner === false)
                              }
                              breed={breed}
                              raceFinished={
                                saddleSelections.length === scratches?.length ||
                                (hasWinner &&
                                  currentSaddleSelection.winner === false) ||
                                (enableMyBetsBehavior &&
                                  ["lost", "won", "attention"].includes(
                                    raceStatus ?? ""
                                  ))
                              }
                              key={`Saddle-num-${currentSaddleSelection.number}`}
                              number={
                                (
                                  favoriteRunner as unknown as Record<
                                    string,
                                    string
                                  >
                                )?.runnerNumber
                              }
                              size="s"
                              mr={
                                !showBetType &&
                                index === rangedSelections.length
                                  ? 0
                                  : "space-1"
                              }
                            />
                          </>
                        )}
                      {wagerDetails && (
                        <SaddleDetailsParagraph
                          qaLabel="details-runner-name"
                          scratched={
                            !!currentSaddleSelection.scratched &&
                            !showReplacedRunners &&
                            !scratches?.includes(item)
                          }
                          {...runnerDetailsStyle}
                        >
                          {showReplacedRunners && scratches?.includes(item)
                            ? (
                                favoriteRunner as unknown as Record<
                                  string,
                                  string
                                >
                              )?.runnerName
                            : (currentSaddleSelection.runnerName as string)}
                        </SaddleDetailsParagraph>
                      )}

                      {wagerDetails &&
                        !!currentSaddleSelection.willPay &&
                        !hideWillPaysTag && (
                          <WillPaysContainer>
                            <Paragraph
                              qaLabel="details-will-pay-amount"
                              textAlign="center"
                              {...runnerDetailsStyle}
                            >
                              {formatCurrency(currentSaddleSelection.willPay)}
                            </Paragraph>
                          </WillPaysContainer>
                        )}

                      {wagerDetails &&
                        !!shouldShowRefundTag &&
                        !!currentSaddleSelection.scratched && (
                          <Tag label="REFUND" size="s" variant="warning" />
                        )}

                      {wagerDetails &&
                        currentSaddleSelection.winner &&
                        currentSaddleSelection.currentOdds &&
                        showOdds && (
                          <WillPaysContainer>
                            <Paragraph
                              qaLabel="details-will-pay-amount"
                              textAlign="center"
                              fontWeight={
                                currentSaddleSelection.winner
                                  ? "bold"
                                  : undefined
                              }
                            >
                              <>
                                {get(
                                  currentSaddleSelection,
                                  "currentOdds.numerator"
                                )}
                                {get(
                                  currentSaddleSelection,
                                  "currentOdds.denominator"
                                )
                                  ? `/${get(
                                      currentSaddleSelection,
                                      "currentOdds.denominator"
                                    )}`
                                  : ""}
                              </>
                            </Paragraph>
                          </WillPaysContainer>
                        )}
                    </>
                  )
                )}
              </RowContainer>
            );
          })}
          {(showAlternatePlaceholder || alternateSelections?.length > 0) &&
            isAlternateView &&
            !wagerDetails && (
              <AlternateContainer>
                <AlternateDivider isDark={isDark} />
                {alternateSelections?.length > 0 ? (
                  alternateSelections.map((item) => (
                    // @ts-ignore
                    <IconContainerAlternate
                      qaLabel="alternate-saddle-row"
                      key={`alternate-container-${item.number}`}
                    >
                      <IconWrapper>
                        <Icon
                          name="doubleChevronLeftRight"
                          backgroundColor="#fff"
                          size="s"
                          lineColor="#05285A"
                        />
                      </IconWrapper>
                      <Saddle
                        isScratched={scratches?.includes(
                          item.number.replace("B", "")
                        )}
                        raceFinished={false}
                        number={item.number}
                        breed={breed}
                        numberColor={item.numberColor}
                        saddleColor={item.saddleColor}
                        size="s"
                      />
                    </IconContainerAlternate>
                  ))
                ) : (
                  <>
                    {showAlternatePlaceholder && (
                      // @ts-ignore
                      <IconContainerAlternate qaLabel="alternate-saddle-row">
                        <IconWrapper>
                          <Icon
                            name="doubleChevronLeftRight"
                            backgroundColor="#fff"
                            size="s"
                            lineColor="#05285A"
                          />
                        </IconWrapper>
                        <Saddle
                          number="?"
                          size="s"
                          numberColor="var(--fd-colors-core-white)"
                          saddleColor="#fff3"
                          qaLabel="saddle-question-mark"
                        />
                      </IconContainerAlternate>
                    )}
                  </>
                )}
              </AlternateContainer>
            )}
        </SaddleSelectionContainer>
      )}
    </>
  );
};

export default SaddleNumbers;
